import { useState } from 'react';
import { Check, Flame, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LinkedHabit } from '@/hooks/useActionLinkedHabits';
import { format } from 'date-fns';

interface LinkedHabitsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  actionText: string;
  habits: LinkedHabit[];
  onAddHabit: () => void;
}

export const LinkedHabitsPopover = ({
  isOpen,
  onClose,
  actionText,
  habits,
  onAddHabit,
}: LinkedHabitsPopoverProps) => {
  const today = format(new Date(), 'yyyy-MM-dd');

  const isCompletedToday = (habit: LinkedHabit) => {
    return habit.completedDates.includes(today);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Habits Contributing to This Action
          </DialogTitle>
        </DialogHeader>

        {/* Action context */}
        <p className="text-sm text-muted-foreground line-clamp-2 -mt-2">
          {actionText}
        </p>

        {/* Habits list */}
        <div className="space-y-3 mt-2">
          {habits.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No habits linked to this action yet.
            </p>
          ) : (
            habits.map((habit) => {
              const completed = isCompletedToday(habit);
              return (
                <div
                  key={habit.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                    completed 
                      ? "bg-primary/5 border-primary/20" 
                      : "bg-muted/30 border-border"
                  )}
                >
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center shrink-0",
                    completed 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted text-muted-foreground"
                  )}>
                    {completed ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-current" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "font-medium text-sm truncate",
                      completed && "text-primary"
                    )}>
                      {habit.title}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-500" />
                      {habit.streak}-day streak
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAddHabit}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Link Another Habit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
